import { createContext, useContext, useMemo } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import type { Me, TenantSummary } from '@iot-dashboard/shared';
import { useMe, useSites } from '../api/hooks';
import { ErrorState, Loading } from '../components/States';
import { AppShell } from './AppShell';
import { mspNav, tenantNav } from './nav';

export interface TenantContextValue {
  tenantId: string;
  tenant: TenantSummary;
  me: Me;
  base: string;
  isMsp: boolean;
  /** MSP admins and tenant admins may change things; viewers may not. */
  canConfigure: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

export function useTenantContext(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenantContext must be used inside TenantLayout');
  return ctx;
}

export function TenantLayout({ mspName }: { mspName: string }) {
  const { tenantId = '' } = useParams();
  const me = useMe();
  const sites = useSites(tenantId);

  const value = useMemo<TenantContextValue | null>(() => {
    if (!me.data) return null;
    const tenant = me.data.tenants.find((t) => t.tenantId === tenantId);
    if (!tenant) return null;
    return {
      tenantId,
      tenant,
      me: me.data,
      base: `/t/${tenantId}`,
      isMsp: me.data.isMsp,
      canConfigure: me.data.isMsp || me.data.role === 'tenant-admin',
    };
  }, [me.data, tenantId]);

  if (me.isPending) return <Loading label="Loading your dashboard…" />;
  if (me.isError) return <ErrorState error={me.error} retry={() => me.refetch()} />;
  if (!value) {
    // Tenant users can only ever open their own tenant.
    const own = me.data.tenants[0];
    return <Navigate to={me.data.isMsp ? '/msp' : own ? `/t/${own.tenantId}/overview` : '/login'} replace />;
  }

  const sections = [...tenantNav(value.base, { canConfigure: value.canConfigure }), ...(value.isMsp ? [mspNav()] : [])];

  return (
    <TenantContext.Provider value={value}>
      <AppShell me={value.me} mspName={mspName} tenant={value.tenant} sites={sites.data} sections={sections}>
        <div className="page">
          <Outlet />
        </div>
      </AppShell>
    </TenantContext.Provider>
  );
}
